import { createFileRoute, Link } from "@tanstack/react-router";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

const slug = "claude-code-image-generation";
const path = `/blog/${slug}`;

export const Route = createFileRoute(path)({
	component: Article,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: path });
		return {
			meta: [
				{
					title:
						"How to Generate Images from Claude Code via MCP - AgentFramer",
				},
				{
					name: "description",
					content:
						"Wire up AgentFramer's MCP server to Claude Code in two minutes and let it generate, verify, and embed images without leaving the terminal.",
				},
				{
					property: "og:title",
					content: "How to Generate Images from Claude Code via MCP",
				},
				{
					property: "og:description",
					content:
						"A working how-to: register the MCP server, ship a real prompt, inspect the tool call, and verify the result.",
				},
				{ property: "og:image", content: ogImageUrl(`blog-${slug}`) },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "article" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl(`blog-${slug}`) },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: path }),
		};
	},
});

function Article() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<p className="text-xs text-muted-foreground/60">April 2026</p>
				<h1 className="text-2xl font-medium tracking-tight">
					How to generate images from Claude Code via MCP
				</h1>
				<p className="text-sm text-muted-foreground">
					You are five minutes from shipping a README and you still need a hero
					image. Instead of context-switching to a UI, you tell Claude Code to
					generate one, verify it, and commit the URL. Here is the exact setup,
					the exact prompt, and the exact JSON Claude sends.
				</p>
			</div>

			<div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
				<h2 className="text-base font-medium text-foreground pt-2">
					The 90-second setup
				</h2>
				<p>
					Claude Code speaks MCP. AgentFramer ships an MCP server that exposes{" "}
					<code>generate_image</code>, <code>generate_video</code>,{" "}
					<code>generate_audio</code>, <code>get_generation</code>,{" "}
					<code>list_recent_generations</code>, <code>get_credits</code>, and a
					handful of workspace tools. Register it once and every Claude Code
					session in that project can call them.
				</p>
				<p>
					Sign up at agentframer.com. New accounts get a small free balance,
					enough to run roughly 30 FLUX schnell generations or 5 FLUX 1.1 pro
					generations end-to-end. The dashboard prints your MCP URL on first
					load and shows live status for every connected agent.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Register the server
				</h2>
				<p>
					From the project root, run the CLI command. Claude Code handles the
					OAuth handshake in your browser, so you never paste an API key into a
					config file.
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">
					{`# add AgentFramer at the project scope
claude mcp add --scope project agentframer https://mcp.agentframer.com/mcp

# verify it landed
claude mcp list`}
				</pre>
				<p>
					Prefer JSON? Drop the same entry into <code>~/.claude/mcp.json</code>{" "}
					for a global install or <code>.claude/mcp.json</code> next to the repo
					for project scope.
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">
					{`{
  "mcpServers": {
    "agentframer": {
      "type": "http",
      "url": "https://mcp.agentframer.com/mcp"
    }
  }
}`}
				</pre>
				<p>
					Open Claude Code and type <code>/mcp</code>. The slash menu lists
					every connected server with a status dot, the tool count, and an
					"authenticate" action if OAuth has not completed. Pick{" "}
					<code>agentframer</code>, complete the browser flow, and the dot turns
					green. The full reference is in the{" "}
					<Link
						to="/help/mcp"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						MCP setup guide
					</Link>
					.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					A real prompt, end to end
				</h2>
				<p>Type this into Claude Code:</p>
				<p className="text-foreground">
					"Generate a 1280x800 dark hero image for the README that reads
					'Self-host or hosted, your call' over a subtle dot grid. Use FLUX 1.1
					pro. Save the URL to <code>docs/hero.url</code> and download the PNG
					to <code>docs/hero.png</code>."
				</p>
				<p>Claude expands that into four steps without further prompting:</p>
				<ol className="list-decimal pl-5 space-y-1">
					<li>
						Calls <code>generate_image</code> with the prompt, model, and
						dimensions.
					</li>
					<li>
						Polls <code>get_generation</code> every few seconds until status is{" "}
						<code>completed</code> (FLUX 1.1 pro typically returns in 6–12s).
					</li>
					<li>
						Writes the signed URL to <code>docs/hero.url</code> with its{" "}
						<code>Write</code> tool.
					</li>
					<li>
						Shells out to <code>curl</code> to fetch the PNG and saves it to{" "}
						<code>docs/hero.png</code>.
					</li>
				</ol>

				<h2 className="text-base font-medium text-foreground pt-2">
					The tool call Claude actually sends
				</h2>
				<p>
					If you expand the tool call in Claude Code's transcript, the payload
					looks roughly like this:
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">
					{`{
  "tool": "generate_image",
  "arguments": {
    "model": "flux-1.1-pro",
    "prompt": "Dark themed README hero, the words 'Self-host or hosted, your call' set in a clean sans-serif, subtle dot grid background, deep charcoal #0a0a0a, soft cyan accent, no logo",
    "width": 1280,
    "height": 800,
    "guidance_scale": 3.5,
    "seed": 42
  }
}`}
				</pre>
				<p>
					AgentFramer returns a generation ID immediately. Claude pings{" "}
					<code>get_generation</code> with that ID until the response carries a{" "}
					<code>url</code> field. At FLUX 1.1 pro list price (around $0.04 per
					image at 1MP), this round trip costs less than five cents. Switching
					the model to <code>flux-schnell</code> drops it to roughly $0.003 per
					image and finishes in 1–2 seconds, which is what you want for
					iteration. Per-call billing means a stalled session never ticks over a
					meter; you pay for the generations you actually run.
				</p>
				<p>
					Two details worth knowing. First, the URL Claude writes to{" "}
					<code>docs/hero.url</code> is signed and time-limited; AgentFramer
					stores the underlying asset for you, so a permanent link is one{" "}
					<code>get_generation</code> call away. Second, the seed in the payload
					is yours to set. Pin it to a number when you find a composition you
					like, and small prompt edits will produce variations instead of
					completely different images. That is how you go from "close" to "ship"
					without burning credits.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Let Claude grade its own homework
				</h2>
				<p>
					Once the PNG is on disk, Claude can read it back. Claude Code's image
					support means you can say "open <code>docs/hero.png</code> and tell me
					whether the headline is legible and centered." It will inspect the
					file, describe what it sees, and propose a revised prompt if the
					rendering is off. That feedback loop, generation followed by visual
					verification, is the part most non-MCP workflows skip.
				</p>
				<p>
					A typical revision sequence: first call uses <code>flux-schnell</code>{" "}
					to scout composition, Claude looks at the result, then it escalates to{" "}
					<code>flux-1.1-pro</code> with a tightened prompt and a fixed seed for
					the keeper. Two calls, under a dime. If the text rendering matters
					(badges, labels, README banners), Claude will often pick Ideogram v2
					or SD3.5 Large on its own; both handle in-image typography better than
					the FLUX family. Tell Claude which model it picked is fine, because
					the next iteration will follow your steer.
				</p>
				<p>
					This is also where <code>list_recent_generations</code> earns its
					keep. If you walk away mid-session, Claude can rehydrate state by
					listing your last few generations, reading their URLs, and picking up
					where the previous turn left off — no scrolling through old terminal
					output, no re-prompting from scratch.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					A cost ceiling pattern for CLAUDE.md
				</h2>
				<p>
					Image calls are billed per generation, so put the budget rules in
					context where Claude will read them. Drop this into your project's{" "}
					<code>CLAUDE.md</code>:
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">
					{`## Image generation policy

- Default model: flux-schnell. It is cheap (~$0.003) and fast.
- Escalate to flux-1.1-pro only when I ask for "final" or "ship".
- Never use sora-2 or veo-3 without explicit approval.
- Before any batch >= 5 calls, run get_credits and report the balance.
- Always save the generation URL alongside the file, not just the file.`}
				</pre>
				<p>
					Claude reads CLAUDE.md on session start and respects it. If you ever
					want a one-off override, just say so in the message; the policy is a
					default, not a lock. Pair it with a <code>get_credits</code> call at
					the top of any heavy session and you have a soft budget alarm that
					costs nothing to enforce. Teams running shared workspaces should also
					pin a target workspace with <code>switch_workspace</code> in the same
					file so generations land in the right billing bucket.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					When MCP image generation in Claude Code is the wrong tool
				</h2>
				<p>
					Claude Code is a great hands on agent for code-adjacent assets: hero
					images, OG cards, empty-state placeholders, doc diagrams, favicons. It
					is the wrong tool for several other jobs.
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<span className="text-foreground">Heavy art direction.</span> If you
						need to iterate on lighting, lens, and pose for an editorial shot,
						the AgentFramer web UI gives you a side-by-side gallery and
						parameter sliders that beat a chat transcript.
					</li>
					<li>
						<span className="text-foreground">Brand-locked aesthetics.</span>{" "}
						Cursor's inline preview and reference-image pinning make
						style-locked work easier than terminal output.
					</li>
					<li>
						<span className="text-foreground">Batch ecommerce.</span> Generating
						400 product variants belongs in a script that calls the HTTP API
						directly, not in an agent loop where each call costs you a tool
						turn.
					</li>
					<li>
						<span className="text-foreground">Long video.</span> Sora 2, Veo 3,
						and Runway Gen-4 are happy to run from MCP, but Claude Code's
						terminal is a poor place to review minutes of footage. Use the
						dashboard.
					</li>
				</ul>
				<p>
					The decision is rarely "MCP or nothing." It is more often: which
					interface is closest to the work I am already doing? If the asset is a
					byproduct of code I am writing right now, Claude Code wins because the
					agent already has the file context. If the asset is the work — a
					campaign hero, a product photo, a music track — a purpose-built UI
					almost always beats a chat box. Compare on the{" "}
					<Link
						to="/models"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						models page
					</Link>{" "}
					if you want to know which model fits which scenario; the same MCP
					server powers all of them, so you can move freely between Claude Code,
					Cursor, and the dashboard without re-authenticating.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Going further
				</h2>
				<p>
					The same connection covers video and audio. Ask Claude to draft a
					15-second product clip with Veo 3 and an Eleven Labs voiceover and you
					get a single tool-call sequence that lands a finished MP4 in your
					repo. The patterns are the same: prompt, poll, write the URL,
					optionally download. The{" "}
					<Link
						to="/help/tool-call-patterns"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						tool-call patterns guide
					</Link>{" "}
					walks through the multi-modal sequences worth memorizing.
				</p>
				<p>
					One last note on reliability. MCP servers are HTTP under the hood, and
					AgentFramer's endpoint runs in our own infrastructure with retry-aware
					tool semantics: if Claude's first <code>generate_image</code> call
					times out at the network layer, the same call will not double charge
					you. <code>get_generation</code> is the source of truth for whether
					anything actually started. That detail matters when an agent loop is
					making its own decisions about retries — you want idempotence, not
					surprise invoices.
				</p>
				<p className="text-foreground">
					Two minutes of setup, one CLAUDE.md block, and your terminal agent
					ships production assets without ever leaving the editor. Stop tabbing
					into a UI to fetch a hero image. Tell Claude what you want, let it
					call the tool, and ship the README.
				</p>
			</div>
		</article>
	);
}
